
if (!customElements.get('product-addons')) {
  customElements.define(
    'product-addons',
    class ProductAddons extends HTMLElement {
      constructor() {
        super();
        this.addons = [];
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onSizeChange = this.onSizeChange.bind(this);
      }
 
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.productForm = this.form ? this.form.closest('product-form') : null;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
 
        this.setupAddons();
        this.setupEngraving();
        this.setupSizeFiltering();
 
        document.addEventListener('click', this.onDocumentClick);
 
        if (this.form) {
          this.submitListenerTarget = this.productForm || document;
          this.submitListenerTarget.addEventListener('submit', this.onFormSubmit, true);
        }
      }
 
      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        if (this.submitListenerTarget) {
          this.submitListenerTarget.removeEventListener('submit', this.onFormSubmit, true);
        }
        this.sizeInputs.forEach((input) => input.removeEventListener('change', this.onSizeChange));
      }
 
      // ---- Size-based add-on visibility -------------------------------------
      // Add-ons can optionally be restricted to specific bell sizes via a
      // data-applicable-sizes attribute (comma-separated, e.g. "3 lb,5 lb"),
      // populated from the custom.applicable_sizes metafield on the add-on
      // product in Liquid. Empty attribute = always visible.
      //
      // This listens to the same Size variant radio inputs already used
      // elsewhere on the product page (name contains "Size"), so no other
      // markup changes are required to wire this up.
 
      setupSizeFiltering() {
        this.sizeInputs = Array.from(
          document.querySelectorAll('input[type="radio"][name*="Size"]')
        );
 
        this.sizeInputs.forEach((input) => input.addEventListener('change', this.onSizeChange));
 
        const checked = this.sizeInputs.find((input) => input.checked);
        this.applySizeFilter(checked ? this.normalizeSizeLabel(checked) : null);
      }
 
      normalizeSizeLabel(input) {
        // Prefer a visible label's text if present (matches what the metafield
        // values will be authored as, e.g. "3 lb"); fall back to the input value.
        const label = input.labels && input.labels[0];
        const text = label ? label.textContent : input.value;
        return (text || '').trim();
      }
 
      onSizeChange(evt) {
        this.applySizeFilter(this.normalizeSizeLabel(evt.target));
      }
 
      applySizeFilter(selectedSize) {
        this.addons.forEach((addon) => {
          const sizesAttr = addon.el.dataset.applicableSizes || '';
          const allowedSizes = sizesAttr
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
 
          const isRestricted = allowedSizes.length > 0;
          const isAllowed = !isRestricted || !selectedSize || allowedSizes.includes(selectedSize);
 
          addon.el.hidden = !isAllowed;
 
          if (!isAllowed && addon.toggle && addon.toggle.checked) {
            addon.toggle.checked = false;
            addon.el.classList.remove('is-active');
          }
        });
      }
 
      // -------------------------------------------------------------------------
 
      setupAddons() {
        this.querySelectorAll('[data-addon]').forEach((el) => {
          const dataEl = el.querySelector('.ej-addon-data');
          let data = { variants: [], defaultVariantId: null };
          try {
            if (dataEl) data = JSON.parse(dataEl.textContent);
          } catch (e) {
            console.error('product-addons: bad addon data', e);
          }
 
          const toggle = el.querySelector('[data-addon-toggle]');
          const optionsEl = el.querySelector('[data-addon-options]');
          const dropdowns = optionsEl ? Array.from(optionsEl.querySelectorAll('[data-dropdown]')) : [];
 
          const selected = dropdowns.map(() => '');
 
          const addon = { el, data, toggle, optionsEl, dropdowns, selected, variant: null };
          this.addons.push(addon);
 
          this.resolveVariant(addon);
 
          if (toggle) {
            toggle.addEventListener('change', () => {
              el.classList.toggle('is-active', toggle.checked);
            });
          }
 
          dropdowns.forEach((dd, index) => {
            this.setupDropdown(dd, (value) => {
              addon.selected[index] = value;
              this.resolveVariant(addon);
              this.ensureChecked(toggle);
            });
          });
        });
      }
 
      ensureChecked(toggle) {
        if (toggle && !toggle.disabled && !toggle.checked) {
          toggle.checked = true;
          toggle.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
 
      resolveVariant(addon) {
        const variants = addon.data.variants || [];
 
        if (addon.dropdowns.length === 0) {
          addon.variant =
            variants.find((v) => String(v.id) === String(addon.data.defaultVariantId)) ||
            variants[0] ||
            null;
          const priceEl0 = addon.el.querySelector('[data-addon-price]');
          if (priceEl0) {
            if (!addon.variant) priceEl0.textContent = 'Unavailable';
            else if (!addon.variant.available) priceEl0.textContent = 'Sold out';
            else if (addon.variant.price) priceEl0.textContent = addon.variant.price;
          }
          addon.el.classList.toggle('is-unavailable', !!addon.variant && !addon.variant.available);
          return;
        }
 
        const fullySelected = addon.selected.length > 0 && addon.selected.every((v) => v !== '');
        const key = addon.selected.join(' / ');
 
        let match = fullySelected ? variants.find((v) => (v.options || []).join(' / ') === key) : null;
        addon.variant = match || null;
 
        const priceEl = addon.el.querySelector('[data-addon-price]');
        if (priceEl) {
          if (!fullySelected) {
            priceEl.textContent = 'Select options';
          } else if (!addon.variant) {
            priceEl.textContent = 'Unavailable';
          } else if (!addon.variant.available) {
            priceEl.textContent = 'Sold out';
          } else if (addon.variant.price) {
            priceEl.textContent = addon.variant.price;
          }
        }
        addon.el.classList.toggle('is-unavailable', !!addon.variant && !addon.variant.available);
      }
 
      getSelectedAddonItems() {
        const items = [];
        for (const addon of this.addons) {
          if (addon.el.hidden) continue;
          if (!addon.toggle || !addon.toggle.checked) continue;
          if (!addon.variant || !addon.variant.available) return null;
          items.push({ id: addon.variant.id, quantity: 1 });
        }
        return items;
      }
 
      setupEngraving() {
        const root = this.querySelector('[data-engraving]');
        if (!root) return;
 
        const toggle = root.querySelector('[data-engraving-toggle]');
        const fields = root.querySelector('[data-engraving-fields]');
        const tierDropdown = root.querySelector('[data-engraving-tier]');
        const priceLabel = root.querySelector('[data-engraving-price]');
        const note = root.querySelector('[data-engraving-note]');
        const line1Input = root.querySelector('[data-engraving-line="1"]');
        const line2Input = root.querySelector('[data-engraving-line="2"]');
        const line1Field = line1Input ? line1Input.closest('.ej-field') : null;
        const line2Field = line2Input ? line2Input.closest('.ej-field') : null;
 
        this.engraving = {
          toggle,
          style: '',
          variantId: null,
          available: true,
          line1Input,
          line2Input,
          line1Field,
          line2Field,
        };
 
        this.applyLineVisibility(0);
 
        if (toggle && fields) {
          toggle.addEventListener('change', () => {
            fields.hidden = !toggle.checked;
            root.classList.toggle('is-active', toggle.checked);
          });
        }
 
        if (tierDropdown) {
          this.setupDropdown(tierDropdown, (value, optionEl) => {
            this.engraving.style = value || '';
            this.engraving.variantId = optionEl.dataset.variantId || null;
            this.engraving.available = optionEl.dataset.available !== 'false';
            if (priceLabel && optionEl.dataset.price) priceLabel.textContent = optionEl.dataset.price;
            if (note) {
              note.hidden = false;
              if (optionEl.dataset.note) note.textContent = optionEl.dataset.note;
            }
            this.applyLineVisibility(optionEl.dataset.lines);
            this.ensureChecked(toggle);
          });
        }
 
        [line1Input, line2Input].forEach((input) => {
          if (input) input.addEventListener('input', () => this.ensureChecked(toggle));
        });
      }
 
      applyLineVisibility(count) {
        const eng = this.engraving;
        if (!eng) return;
        const n = parseInt(count, 10);
        const lines = Number.isNaN(n) ? 1 : n;
        if (eng.line1Field) eng.line1Field.hidden = lines < 1;
        if (eng.line2Field) eng.line2Field.hidden = lines < 2;
        if (lines < 1 && eng.line1Input) eng.line1Input.value = '';
        if (lines < 2 && eng.line2Input) eng.line2Input.value = '';
      }
 
      visibleEngravingInputs() {
        const eng = this.engraving;
        if (!eng) return [];
        return [eng.line1Input, eng.line2Input].filter((input) => input && input.offsetParent !== null);
      }
 
      buildEngravingProperties() {
        const eng = this.engraving;
        const properties = { 'Engraving Style': eng.style };
        this.visibleEngravingInputs().forEach((input, index) => {
          const value = input.value.trim();
          if (value) properties['Line ' + (index + 1)] = value;
        });
        return properties;
      }
 
      getEngravingItem() {
        const eng = this.engraving;
        if (!eng || !eng.toggle || !eng.toggle.checked) return null;
        if (!eng.variantId || !eng.available) return null;
        return { id: eng.variantId, quantity: 1, properties: this.buildEngravingProperties() };
      }
 
      getEngravingMainProperties() {
        const eng = this.engraving;
        if (!eng || !eng.toggle || !eng.toggle.checked) return null;
        if (!eng.style || !eng.style.trim()) return null;
        if (eng.variantId && eng.available) return null;
        return this.buildEngravingProperties();
      }
 
      focusEngravingError() {
        const eng = this.engraving;
        if (!eng) return;
        const root = this.querySelector('[data-engraving]');
        const fields = root ? root.querySelector('[data-engraving-fields]') : null;
        if (eng.toggle && eng.toggle.checked && fields) fields.hidden = false;
        if (eng.line1Input && !eng.line1Input.value.trim()) eng.line1Input.focus();
      }
 
      validateEngraving() {
        const eng = this.engraving;
        if (!eng || !eng.toggle || !eng.toggle.checked) return null;
        if (!eng.style || !eng.style.trim()) {
          return 'Please choose an engraving style.';
        }
        if (eng.variantId && !eng.available) {
          return 'The selected engraving style is sold out. Please choose another.';
        }
        if (eng.line1Input && eng.line1Input.offsetParent !== null && !eng.line1Input.value.trim()) {
          return 'Please complete the engraving text.';
        }
        return null;
      }
 
      setupDropdown(dropdown, onSelect) {
        const btn = dropdown.querySelector('.ej-dropdown__btn');
        const menu = dropdown.querySelector('.ej-dropdown__menu');
        const valueEl = dropdown.querySelector('[data-dropdown-value]');
        if (!btn || !menu) return;
 
        const options = Array.from(menu.querySelectorAll('.ej-dropdown__option'));
        options.forEach((o) => o.setAttribute('tabindex', '-1'));
 
        const close = (focusBtn) => {
          menu.hidden = true;
          btn.setAttribute('aria-expanded', 'false');
          dropdown.classList.remove('is-open');
          if (focusBtn) btn.focus();
        };
 
        const open = () => {
          this.querySelectorAll('.ej-dropdown.is-open').forEach((d) => {
            if (d !== dropdown) {
              d.classList.remove('is-open');
              const m = d.querySelector('.ej-dropdown__menu');
              const b = d.querySelector('.ej-dropdown__btn');
              if (m) m.hidden = true;
              if (b) b.setAttribute('aria-expanded', 'false');
            }
          });
          menu.hidden = false;
          btn.setAttribute('aria-expanded', 'true');
          dropdown.classList.add('is-open');
          const current = options.find((o) => o.getAttribute('aria-selected') === 'true') || options[0];
          if (current) current.focus();
        };
 
        const select = (optionEl) => {
          options.forEach((o) => o.setAttribute('aria-selected', String(o === optionEl)));
          if (valueEl) valueEl.textContent = optionEl.dataset.value || optionEl.textContent.trim();
          btn.classList.remove('ej-dropdown__btn--placeholder');
          close(true);
          onSelect(optionEl.dataset.value, optionEl);
        };
 
        btn.addEventListener('click', () => {
          if (menu.hidden) open();
          else close(true);
        });
 
        btn.addEventListener('keydown', (e) => {
          if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
            e.preventDefault();
            open();
          }
        });
 
        options.forEach((opt) => {
          opt.addEventListener('click', () => select(opt));
          opt.addEventListener('keydown', (e) => {
            const idx = options.indexOf(opt);
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              (options[idx + 1] || options[0]).focus();
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              (options[idx - 1] || options[options.length - 1]).focus();
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              select(opt);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              close(true);
            } else if (e.key === 'Tab') {
              close(false);
            }
          });
        });
      }
 
      onDocumentClick(evt) {
        if (this.contains(evt.target)) {
          const openDropdown = evt.target.closest('.ej-dropdown.is-open');
          this.querySelectorAll('.ej-dropdown.is-open').forEach((d) => {
            if (d !== openDropdown) {
              d.classList.remove('is-open');
              const m = d.querySelector('.ej-dropdown__menu');
              const b = d.querySelector('.ej-dropdown__btn');
              if (m) m.hidden = true;
              if (b) b.setAttribute('aria-expanded', 'false');
            }
          });
          return;
        }
        this.querySelectorAll('.ej-dropdown.is-open').forEach((d) => {
          d.classList.remove('is-open');
          const m = d.querySelector('.ej-dropdown__menu');
          const b = d.querySelector('.ej-dropdown__btn');
          if (m) m.hidden = true;
          if (b) b.setAttribute('aria-expanded', 'false');
        });
      }
 
      onFormSubmit(evt) {
        if (evt.target !== this.form) return;
 
        const engravingError = this.validateEngraving();
        if (engravingError) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this.focusEngravingError();
          this.showError(engravingError);
          return;
        }
 
        const addonItems = this.getSelectedAddonItems();
 
        if (addonItems === null) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this.showError('Please choose an option for each add-on you’ve selected, and remove any that are sold out.');
          return;
        }
 
        const engravingItem = this.getEngravingItem();
        const engravingMainProps = this.getEngravingMainProperties();
        const extras = [...addonItems];
        if (engravingItem) extras.push(engravingItem);
 
        if (extras.length === 0 && !engravingMainProps) {
          return;
        }
 
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.addToCart(evt, extras, engravingMainProps);
      }
 
      addToCart(evt, addonItems, mainExtraProps) {
        const submitButton = this.form.querySelector('[type="submit"]');
        const spinner = submitButton ? submitButton.querySelector('.loading__spinner') : null;
 
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
        }
        if (spinner) spinner.classList.remove('hidden');
        this.showError(false);
 
        const fd = new FormData(this.form);
        const mainItem = {
          id: fd.get('id'),
          quantity: parseInt(fd.get('quantity'), 10) || 1,
          properties: {},
        };
        for (const [name, value] of fd.entries()) {
          const match = name.match(/^properties\[(.+)\]$/);
          if (match && value !== '') mainItem.properties[match[1]] = value;
        }
        if (mainExtraProps) Object.assign(mainItem.properties, mainExtraProps);
        if (fd.get('selling_plan')) mainItem.selling_plan = fd.get('selling_plan');
        if (Object.keys(mainItem.properties).length === 0) delete mainItem.properties;
 
        const body = { items: [mainItem, ...addonItems] };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }
 
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);
 
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              return;
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            
            if (!response.key && response.items && response.items[0]) {
              response.key = response.items[0].key;
            }
            this.cart.renderContents(response);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-addons', cartData: response });
            }
          })
          .catch((e) => console.error(e))
          .finally(() => {
            if (submitButton) {
              submitButton.classList.remove('loading');
              submitButton.removeAttribute('aria-disabled');
            }
            if (spinner) spinner.classList.add('hidden');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          });
      }
 
      showError(message) {
        const productForm = this.form ? this.form.closest('product-form') : null;
        if (!productForm) return;
        const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
        const messageEl = productForm.querySelector('.product-form__error-message');
        if (!wrapper) return;
        wrapper.toggleAttribute('hidden', !message);
        if (message && messageEl) messageEl.textContent = message;
      }
    }
  );
}